import { useRef } from 'react';
import { WireframeBackground } from '@/components/WireframeBackground';

// ---------------------------------------------------------------------------
// Showcase URL — set this to a real public model URL with ?mode=showcase
// e.g. "/playground/username/model-slug?mode=showcase"
// Leave as undefined to show a gradient placeholder instead.
// TODO: replace with a real published Meshpit model URL
// ---------------------------------------------------------------------------
const HERO_SHOWCASE_URL: string | undefined = undefined;
import { Link } from 'react-router-dom';
import { m, useInView } from 'framer-motion';
import {
  Code2,
  Box,
  Share2,
  Sparkles,
  ShieldCheck,
  FileStack,
  Download,
  History,
  Bookmark,
  Library,
  DatabaseZap,
  ImageDown,
  DollarSign,
  BookOpen,
  Palette,
  SlidersHorizontal,
  ToggleLeft,
  List,
  AlignLeft,
  Layers,
  Hash,
  Compass,
  MousePointerClick,
  LayoutGrid,
  Eye,
  Lock,
  Type,
  Upload,
  PenLine,
  Plus,
  Gem,
  Sun,
  Settings2,
  Camera,
  Move3D,
  Copy,
  Radio,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { MeshpitLogo } from '@/components/MeshpitLogo';

// ---------------------------------------------------------------------------
// Animation helpers
// ---------------------------------------------------------------------------

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0 },
};

function FadeIn({
  children,
  delay = 0,
  className,
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <m.div
      ref={ref}
      variants={fadeUp}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      transition={{ duration: 0.5, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </m.div>
  );
}

// ---------------------------------------------------------------------------
// Browser mockup
// ---------------------------------------------------------------------------

function BrowserMockup({
  gradient,
  label,
  showcaseUrl,
}: {
  gradient: string;
  label?: string;
  /** If provided, render a live iframe instead of the gradient placeholder */
  showcaseUrl?: string;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card shadow-2xl">
      {/* fake browser chrome */}
      <div className="flex items-center gap-2 border-b border-border bg-muted/60 px-4 py-2.5">
        <span className="h-2.5 w-2.5 rounded-full bg-red-400/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-yellow-400/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-green-400/70" />
        <div className="mx-3 flex-1 rounded-md bg-background/60 px-3 py-1 text-xs text-muted-foreground/60">
          meshpit.app
        </div>
      </div>
      {/* content area */}
      {showcaseUrl ? (
        <div className="relative aspect-video w-full overflow-hidden">
          <iframe
            src={showcaseUrl}
            className="h-full w-full border-0"
            title="Live 3D model showcase"
            loading="lazy"
            allow="accelerometer"
          />
        </div>
      ) : (
        <div
          className={`relative flex aspect-video items-center justify-center ${gradient}`}
        >
          {label && (
            <span className="rounded-lg bg-background/20 px-4 py-2 text-sm font-medium text-white/70 backdrop-blur-sm">
              {label}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Value prop card
// ---------------------------------------------------------------------------

function ValueCard({
  icon: Icon,
  title,
  description,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
}) {
  return (
    <div className="flex h-full flex-col items-start gap-3 rounded-xl border border-border bg-card p-6">
      <div className="rounded-lg bg-muted p-2.5">
        <Icon className="h-5 w-5 text-foreground" />
      </div>
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
      <p className="text-sm leading-relaxed text-muted-foreground">
        {description}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Secondary feature card
// ---------------------------------------------------------------------------

function FeatureChip({
  icon: Icon,
  title,
  description,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
}) {
  return (
    <div className="flex h-full gap-4 rounded-xl border border-border bg-card p-5">
      <div className="mt-0.5 shrink-0 rounded-md bg-muted p-2">
        <Icon className="h-4 w-4 text-foreground" />
      </div>
      <div>
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Feature row (alternating layout)
// ---------------------------------------------------------------------------

function FeatureRow({
  badge,
  title,
  description,
  gradient,
  mockupLabel,
  reverse = false,
  delay = 0,
}: {
  badge?: string;
  title: string;
  description: string;
  gradient: string;
  mockupLabel?: string;
  reverse?: boolean;
  delay?: number;
}) {
  return (
    <FadeIn delay={delay}>
      <div
        className={`flex flex-col items-center gap-10 lg:flex-row ${reverse ? 'lg:flex-row-reverse' : ''}`}
      >
        {/* text */}
        <div className="flex flex-1 flex-col gap-4">
          {badge && (
            <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-border bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
              <Sparkles className="h-3 w-3" />
              {badge}
            </span>
          )}
          <h3 className="text-2xl font-semibold tracking-tight text-foreground">
            {title}
          </h3>
          <p className="text-base leading-relaxed text-muted-foreground">
            {description}
          </p>
        </div>
        {/* mockup */}
        <div className="w-full flex-1 lg:max-w-[520px]">
          <BrowserMockup gradient={gradient} label={mockupLabel} />
        </div>
      </div>
    </FadeIn>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function LandingPage() {
  return (
    <div className="relative h-full overflow-y-auto">
      <WireframeBackground />
      <div className="relative z-10 mx-auto max-w-6xl px-6 pb-32">
        {/* ---------------------------------------------------------------- */}
        {/* HERO                                                              */}
        {/* ---------------------------------------------------------------- */}
        <section className="flex flex-col items-center py-24 text-center">
          <FadeIn>
            <div className="mb-6 flex items-center justify-center">
              <MeshpitLogo className="h-14 w-14 text-foreground" />
            </div>
          </FadeIn>

          <FadeIn delay={0.05}>
            <p className="mb-5 text-sm font-semibold uppercase tracking-widest text-muted-foreground/70">
              OpenSCAD, extended and reimagined
            </p>
          </FadeIn>

          <FadeIn delay={0.08}>
            <h1 className="max-w-2xl text-5xl font-bold tracking-tight text-foreground sm:text-6xl">
              Welcome to Meshpit
            </h1>
          </FadeIn>

          <FadeIn delay={0.13}>
            <p className="mt-4 text-2xl font-semibold tracking-tight text-muted-foreground sm:text-3xl">
              Code it. Share it. Customize it. Sell it.
            </p>
          </FadeIn>

          <FadeIn delay={0.18}>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
              A platform for parametric 3D models — write OpenSCAD or generate
              with AI, publish with live parameters, let anyone customize your
              design right in the browser, and sell your models directly to your
              audience. No installation required.
            </p>
          </FadeIn>

          <FadeIn delay={0.2}>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <Link to="/new">
                <Button size="lg" className="gap-2 px-7">
                  Start Creating
                  <span aria-hidden>→</span>
                </Button>
              </Link>
              <Link to="/explore">
                <Button size="lg" variant="outline" className="gap-2 px-7">
                  Explore Models
                </Button>
              </Link>
              <Link to="/features">
                <Button size="lg" variant="outline" className="gap-2 px-7">
                  See All Features
                </Button>
              </Link>
              <Link to="/blog">
                <Button size="lg" variant="outline" className="gap-2 px-7">
                  Read the Blog
                </Button>
              </Link>
            </div>
          </FadeIn>

          <FadeIn delay={0.25} className="mt-16 w-full max-w-4xl">
            <BrowserMockup
              gradient="bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900"
              label="Live 3D preview with interactive parameters"
              showcaseUrl={HERO_SHOWCASE_URL}
            />
          </FadeIn>
        </section>

        {/* ---------------------------------------------------------------- */}
        {/* VALUE PROPS                                                       */}
        {/* ---------------------------------------------------------------- */}
        <section className="py-16">
          <FadeIn>
            <div className="mb-4 text-center">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                How it works
              </p>
            </div>
          </FadeIn>

          <div className="grid items-stretch gap-5 sm:grid-cols-3">
            <FadeIn delay={0} className="h-full">
              <ValueCard
                icon={Code2}
                title="Write OpenSCAD"
                description="Full-featured Monaco editor with syntax highlighting, autocomplete, and inline error markers — the same engine that powers VS Code."
              />
            </FadeIn>
            <FadeIn delay={0.07} className="h-full">
              <ValueCard
                icon={Box}
                title="See it Instantly"
                description="OpenSCAD compiled to WebAssembly runs entirely in your browser. Zero server roundtrip — changes render in real-time as you type."
              />
            </FadeIn>
            <FadeIn delay={0.14} className="h-full">
              <ValueCard
                icon={Share2}
                title="Share with a URL"
                description="Every model gets a clean, shareable link. Fork anyone's public design, remix it, and publish your own variation in seconds."
              />
            </FadeIn>
          </div>
        </section>

        <FadeIn>
          <Separator className="my-4" />
        </FadeIn>

        {/* ---------------------------------------------------------------- */}
        {/* FEATURE ROWS                                                      */}
        {/* ---------------------------------------------------------------- */}
        <section className="flex flex-col gap-24 py-16">
          <FadeIn>
            <div className="text-center">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Features
              </p>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-foreground">
                Everything you need to design, share, and iterate
              </h2>
            </div>
          </FadeIn>

          <FeatureRow
            badge="AI-Powered"
            title="Describe it. Build it."
            description="Use any AI model via OpenRouter — GPT-4o, Claude, Gemini and more — to generate or modify OpenSCAD code from plain English. Review the plan before it writes a single line, watch the code stream in, and iterate until it's exactly right."
            gradient="bg-gradient-to-br from-violet-900 via-purple-800 to-indigo-900"
            mockupLabel="AI code generation"
            delay={0}
          />

          <FeatureRow
            title="Click to Customize"
            description="Parameters are automatically extracted from your code and turned into purpose-built controls — sliders, angle dials, XY and XYZ pads, steppers, colour pickers, grid inputs, and more. A single comment annotation is all it takes: the right widget for the job, not just a slider for everything. Share a URL and anyone can configure your design without touching a line of code."
            gradient="bg-gradient-to-br from-sky-900 via-cyan-800 to-teal-900"
            mockupLabel="Interactive parameter panel"
            reverse
            delay={0}
          />

          <FeatureRow
            title="Professional-Grade Preview"
            description="Multi-color rendering, PBR materials, ambient occlusion, bloom, soft shadows, and wireframe overlay — all running in-browser via Three.js. Add Picture-in-Picture cameras to show multiple angles at once, compose custom scene grids and backgrounds entirely from code, and measure distances directly on the model."
            gradient="bg-gradient-to-br from-slate-900 via-zinc-800 to-neutral-900"
            mockupLabel="3D viewer with post-processing"
            delay={0}
          />

          <FeatureRow
            title="Texture Scripts"
            description="Write JavaScript to generate textures procedurally using the full Canvas 2D API — gradients, noise, patterns, animations. Drive them with the same parameter sliders as your model, preview the result live, and project them onto any geometry."
            gradient="bg-gradient-to-br from-fuchsia-900 via-pink-800 to-rose-900"
            mockupLabel="JS canvas texture preview"
            reverse
            delay={0}
          />

          <FeatureRow
            title="Fork. Remix. Share."
            description="Every public model is forkable with one click. Track changes with full version history and commit messages, restore any previous version, and embed any model in your own website or blog with a single iframe tag. Public or private — you control visibility."
            gradient="bg-gradient-to-br from-emerald-900 via-green-800 to-teal-900"
            mockupLabel="Community model grid"
            reverse
            delay={0}
          />

          <FeatureRow
            badge="AI-Powered"
            title="Explain it. Understand it."
            description="Select any block of OpenSCAD code and ask the AI to explain it in plain English — with syntax-highlighted examples, step-by-step breakdowns, and contextual tips. Great for learning OpenSCAD or making sense of a model you forked."
            gradient="bg-gradient-to-br from-amber-900 via-orange-800 to-yellow-900"
            mockupLabel="AI code explanation"
            delay={0}
          />

          <FeatureRow
            badge="Creator Economy"
            title="Monetize Your Models."
            description="Set a price on your designs and let buyers download the STL directly — payments handled via Stripe, no platform cut beyond Stripe fees. From one-off art pieces to premium Gridfinity templates, turn your CAD skills into income."
            gradient="bg-gradient-to-br from-green-900 via-emerald-800 to-cyan-900"
            mockupLabel="Paid model download"
            reverse
            delay={0}
          />
        </section>

        <FadeIn>
          <Separator className="my-4" />
        </FadeIn>

        {/* ---------------------------------------------------------------- */}
        {/* OPENSCAD EXTENSIONS INFOGRAPHIC                                  */}
        {/* ---------------------------------------------------------------- */}
        <section className="py-16">
          <FadeIn>
            <div className="mb-10 text-center">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Built on OpenSCAD
              </p>
              <h2 className="mt-3 text-2xl font-bold tracking-tight text-foreground">
                Everything OpenSCAD has — plus a lot more
              </h2>
              <p className="mt-3 max-w-xl mx-auto text-sm leading-relaxed text-muted-foreground">
                Meshpit runs real OpenSCAD in the browser. Every standard
                parameter annotation works exactly as documented. On top of
                that, Meshpit adds its own extensions for richer UI controls,
                smarter panels, and interactive 3D.
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.05}>
            <div className="grid gap-4 lg:grid-cols-[1fr_auto_1fr]">

              {/* ── Left column: Standard OpenSCAD ── */}
              <div className="rounded-2xl border border-border bg-card p-6">
                <div className="mb-5 flex items-center gap-2.5">
                  <div className="rounded-md bg-muted p-2">
                    <Code2 className="h-4 w-4 text-foreground" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                      Standard OpenSCAD
                    </p>
                    <p className="text-sm font-semibold text-foreground">
                      Native parameter syntax
                    </p>
                  </div>
                </div>
                <ul className="space-y-2.5">
                  {[
                    { icon: SlidersHorizontal, label: 'Number slider', note: 'height = 42;' },
                    { icon: SlidersHorizontal, label: 'Slider with range / step', note: '// [min:step:max]' },
                    { icon: ToggleLeft,        label: 'Boolean toggle', note: 'add_lid = true;' },
                    { icon: AlignLeft,         label: 'Text input', note: 'label = "Hello";' },
                    { icon: List,              label: 'Dropdown / toggle group', note: '// [a, b, c]' },
                    { icon: List,              label: 'Labelled options', note: '// 16=Draft, 32=Normal' },
                    { icon: Layers,            label: 'Vector sliders', note: '// [x, y, z]' },
                    { icon: Hash,              label: 'Range input', note: 'r = [0:5:360];' },
                    { icon: Layers,            label: 'Parameter groups', note: '/* [Group Name] */' },
                    { icon: Layers,            label: 'Tab groups', note: '/* [Tab: Group] */' },
                    { icon: Code2,             label: 'Stop-parsing sentinel', note: '// --- parameters end ---' },
                    { icon: Palette,           label: 'Color picker', note: 'body_color = [R, G, B];' },
                  ].map(({ icon: Icon, label, note }) => (
                    <li key={label} className="flex items-center gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-muted">
                        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                      </span>
                      <span className="flex-1 text-sm text-foreground">{label}</span>
                      <code className="hidden text-xs text-muted-foreground/70 sm:block font-mono">{note}</code>
                    </li>
                  ))}
                </ul>
              </div>

              {/* ── Center divider ── */}
              <div className="flex flex-col items-center justify-center gap-3 py-4 lg:py-0">
                <div className="hidden h-full w-px bg-border lg:block" />
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border bg-card shadow-sm">
                  <Plus className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="hidden h-full w-px bg-border lg:block" />
              </div>

              {/* ── Right column: Meshpit extensions ── */}
              <div className="rounded-2xl border border-border bg-card p-6">
                <div className="mb-5 flex items-center gap-2.5">
                  <div className="rounded-md bg-violet-500/15 p-2">
                    <Sparkles className="h-4 w-4 text-violet-400" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-violet-400">
                      Meshpit Extensions
                    </p>
                    <p className="text-sm font-semibold text-foreground">
                      Beyond standard OpenSCAD
                    </p>
                  </div>
                </div>
                <ul className="space-y-2.5">
                  {[
                    { icon: Compass,          label: 'Angle dial',            note: '// [-180°:180°]',        color: 'text-sky-400',     bg: 'bg-sky-500/10' },
                    { icon: SlidersHorizontal, label: 'Stepper',              note: '// <16:Draft, 32:Normal>', color: 'text-cyan-400',    bg: 'bg-cyan-500/10' },
                    { icon: MousePointerClick, label: 'XY pad',               note: '// [-50:50, -50:50]',    color: 'text-teal-400',    bg: 'bg-teal-500/10' },
                    { icon: MousePointerClick, label: 'XYZ pad',              note: '// [-50:50, -50:50, 0:100]', color: 'text-teal-400', bg: 'bg-teal-500/10' },
                    { icon: Palette,           label: 'Color swatches',       note: '// [#hex1, #hex2, ...]', color: 'text-pink-400',    bg: 'bg-pink-500/10' },
                    { icon: Type,              label: 'Font picker',          note: 'label_font = "…";',      color: 'text-orange-400',  bg: 'bg-orange-500/10' },
                    { icon: Upload,            label: 'File upload',          note: '// upload',              color: 'text-amber-400',   bg: 'bg-amber-500/10' },
                    { icon: PenLine,           label: 'Markdown callout',     note: '// markdown:tip',        color: 'text-indigo-400',  bg: 'bg-indigo-500/10' },
                    { icon: LayoutGrid,        label: 'Grid input',           note: '// grid: 3x2 [...]',     color: 'text-violet-400',  bg: 'bg-violet-500/10' },
                    { icon: PenLine,           label: 'Polygon pad',          note: 'outline = [[x,y], ...]', color: 'text-fuchsia-400', bg: 'bg-fuchsia-500/10' },
                    { icon: Eye,               label: 'Visibility expressions', note: '// [min:max] {expr}', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
                     { icon: Lock,              label: 'Lock annotation',      note: '// @lock',               color: 'text-rose-400',    bg: 'bg-rose-500/10' },
                     { icon: Radio,             label: 'Pub/sub parameter',    note: '// @pubsub',             color: 'text-lime-400',    bg: 'bg-lime-500/10' },
                  ].map(({ icon: Icon, label, note, color, bg }) => (
                    <li key={label} className="flex items-center gap-3">
                      <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded ${bg}`}>
                        <Icon className={`h-3.5 w-3.5 ${color}`} />
                      </span>
                      <span className="flex-1 text-sm text-foreground">{label}</span>
                      <code className="hidden text-xs text-muted-foreground/70 sm:block font-mono">{note}</code>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={0.1}>
            <div className="mt-6 flex justify-center">
              <Link to="/cheatsheet">
                <Button variant="outline" className="gap-2">
                  Open the Parameter Cheatsheet
                  <span aria-hidden>→</span>
                </Button>
              </Link>
            </div>
          </FadeIn>
        </section>

        <FadeIn>
          <Separator className="my-4" />
        </FadeIn>

        {/* ---------------------------------------------------------------- */}
        {/* API EXTENSIONS INFOGRAPHIC                                        */}
        {/* ---------------------------------------------------------------- */}
        <section className="py-16">
          <FadeIn>
            <div className="mb-10 text-center">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Meshpit API
              </p>
              <h2 className="mt-3 text-2xl font-bold tracking-tight text-foreground">
                Control the renderer from your SCAD code
              </h2>
              <p className="mt-3 max-w-xl mx-auto text-sm leading-relaxed text-muted-foreground">
                Meshpit adds 21 custom modules and functions that control how
                your geometry looks in the viewer — materials, lights, scene
                settings, cameras, motion, and more. None of them affect STL
                exports.
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.05}>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">

              {/* Materials */}
              <div className="rounded-2xl border border-border bg-card p-5">
                <div className="mb-4 flex items-center gap-2.5">
                  <div className="rounded-md bg-amber-500/15 p-2">
                    <Gem className="h-4 w-4 text-amber-400" />
                  </div>
                  <p className="text-sm font-semibold text-foreground">Materials</p>
                </div>
                <ul className="space-y-2">
                  {[
                    { label: 'material_def()',        note: 'Define a PBR surface' },
                    { label: 'material() { }',        note: 'Apply surface to geometry' },
                    { label: 'shader_material() { }', note: 'Use shader material' },
                  ].map(({ label, note }) => (
                    <li key={label} className="flex items-baseline gap-2">
                      <code className="shrink-0 rounded bg-amber-500/10 px-1.5 py-0.5 text-xs font-mono text-amber-400">{label}</code>
                      <span className="text-xs text-muted-foreground">{note}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Lights */}
              <div className="rounded-2xl border border-border bg-card p-5">
                <div className="mb-4 flex items-center gap-2.5">
                  <div className="rounded-md bg-yellow-500/15 p-2">
                    <Sun className="h-4 w-4 text-yellow-400" />
                  </div>
                  <p className="text-sm font-semibold text-foreground">Lights</p>
                </div>
                <ul className="space-y-2">
                  {[
                    { label: 'light_ambient()',       note: 'Uniform fill light' },
                    { label: 'light_directional()',   note: 'Parallel sun/sky rays' },
                    { label: 'light_point()',         note: 'Omnidirectional bulb' },
                    { label: 'light_spot()',          note: 'Focused cone of light' },
                  ].map(({ label, note }) => (
                    <li key={label} className="flex items-baseline gap-2">
                      <code className="shrink-0 rounded bg-yellow-500/10 px-1.5 py-0.5 text-xs font-mono text-yellow-400">{label}</code>
                      <span className="text-xs text-muted-foreground">{note}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Scene Settings */}
              <div className="rounded-2xl border border-border bg-card p-5">
                <div className="mb-4 flex items-center gap-2.5">
                  <div className="rounded-md bg-sky-500/15 p-2">
                    <Settings2 className="h-4 w-4 text-sky-400" />
                  </div>
                  <p className="text-sm font-semibold text-foreground">Scene Settings</p>
                </div>
                <ul className="space-y-2">
                  {[
                    { label: 'scene_material()',       note: 'Scene-wide PBR defaults' },
                    { label: 'scene_default_lights()', note: 'Scale the built-in 3-point rig' },
                    { label: 'scene_shadows()',        note: 'Shadow on/off, softness, resolution' },
                    { label: 'scene_bloom()',          note: 'Bloom / glow post-processing' },
                    { label: 'scene_ssao()',           note: 'Screen-space ambient occlusion' },
                    { label: 'scene_background()',     note: 'Viewport background color' },
                    { label: 'scene_camera()',         note: 'Initial camera position and FOV' },
                  ].map(({ label, note }) => (
                    <li key={label} className="flex items-baseline gap-2">
                      <code className="shrink-0 rounded bg-sky-500/10 px-1.5 py-0.5 text-xs font-mono text-sky-400">{label}</code>
                      <span className="text-xs text-muted-foreground">{note}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Cameras & Overlays */}
              <div className="rounded-2xl border border-border bg-card p-5">
                <div className="mb-4 flex items-center gap-2.5">
                  <div className="rounded-md bg-violet-500/15 p-2">
                    <Camera className="h-4 w-4 text-violet-400" />
                  </div>
                  <p className="text-sm font-semibold text-foreground">Cameras &amp; Overlays</p>
                </div>
                <ul className="space-y-2">
                  {[
                    { label: 'camera_pip()',     note: 'Picture-in-picture overlays (up to 4)' },
                    { label: 'svg_overlay()',    note: '2D SVG overlaid on the viewer' },
                    { label: 'annotation() { }', note: 'Floating text label on geometry' },
                  ].map(({ label, note }) => (
                    <li key={label} className="flex items-baseline gap-2">
                      <code className="shrink-0 rounded bg-violet-500/10 px-1.5 py-0.5 text-xs font-mono text-violet-400">{label}</code>
                      <span className="text-xs text-muted-foreground">{note}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Interactivity */}
              <div className="rounded-2xl border border-border bg-card p-5">
                <div className="mb-4 flex items-center gap-2.5">
                  <div className="rounded-md bg-pink-500/15 p-2">
                    <MousePointerClick className="h-4 w-4 text-pink-400" />
                  </div>
                  <p className="text-sm font-semibold text-foreground">Interactivity</p>
                </div>
                <ul className="space-y-2">
                  {[
                    { label: 'interactive() { }',         note: 'Click geometry to update a parameter' },
                    { label: 'interactive_publish() { }', note: 'Click to publish — no recompile' },
                  ].map(({ label, note }) => (
                    <li key={label} className="flex items-baseline gap-2">
                      <code className="shrink-0 rounded bg-pink-500/10 px-1.5 py-0.5 text-xs font-mono text-pink-400">{label}</code>
                      <span className="text-xs text-muted-foreground">{note}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Motion & Cloning */}
              <div className="rounded-2xl border border-border bg-card p-5">
                <div className="mb-4 flex items-center gap-2.5">
                  <div className="rounded-md bg-emerald-500/15 p-2">
                    <Move3D className="h-4 w-4 text-emerald-400" />
                  </div>
                  <p className="text-sm font-semibold text-foreground">Motion &amp; Cloning</p>
                </div>
                <ul className="space-y-2">
                  {[
                    { label: 'motion() { }',   note: 'Animate geometry — translate, orbit, spin, rock' },
                    { label: 'clone_ref() { }', note: 'Tag geometry as a named clone source' },
                    { label: 'clone()',         note: 'Place a static copy at a given transform' },
                  ].map(({ label, note }) => (
                    <li key={label} className="flex items-baseline gap-2">
                      <code className="shrink-0 rounded bg-emerald-500/10 px-1.5 py-0.5 text-xs font-mono text-emerald-400">{label}</code>
                      <span className="text-xs text-muted-foreground">{note}</span>
                    </li>
                  ))}
                </ul>
              </div>

            </div>
          </FadeIn>

          <FadeIn delay={0.1}>
            <div className="mt-6 flex justify-center">
              <Link to="/blog/meshpit-api-reference">
                <Button variant="outline" className="gap-2">
                  Read the API Reference
                  <span aria-hidden>→</span>
                </Button>
              </Link>
            </div>
          </FadeIn>
        </section>

        <FadeIn>
          <Separator className="my-4" />
        </FadeIn>

        {/* ---------------------------------------------------------------- */}
        {/* SECONDARY FEATURES GRID                                          */}
        {/* ---------------------------------------------------------------- */}
        <section className="py-16">
          <FadeIn>
            <div className="mb-10 text-center">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                And much more
              </p>
              <h2 className="mt-3 text-2xl font-bold tracking-tight text-foreground">
                Built for the whole workflow
              </h2>
            </div>
          </FadeIn>

          <div className="grid items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <FadeIn delay={0} className="h-full">
              <FeatureChip
                icon={ShieldCheck}
                title="Printability Analysis"
                description="Automated mesh checks for watertightness, non-manifold geometry, thin walls, and degenerate faces — before you ever hit print."
              />
            </FadeIn>
            <FadeIn delay={0.05} className="h-full">
              <FeatureChip
                icon={FileStack}
                title="Multi-file Projects"
                description="Attach .svg, .dxf, .stl, .dat and more alongside your main .scad file. SVGs are auto-split into individual paths, extract modules into separate files, and capture any mesh directly to the file system."
              />
            </FadeIn>
            <FadeIn delay={0.1} className="h-full">
              <FeatureChip
                icon={Download}
                title="Export STL or SCAD"
                description="Download your model instantly. Multi-color models export as a zip of per-color STL files, ready for multi-material printing. Parameters are bundled with the export too."
              />
            </FadeIn>
            <FadeIn delay={0.05} className="h-full">
              <FeatureChip
                icon={History}
                title="Version History"
                description="Every save creates a version entry. Add commit messages, preview any past version, and restore with a single click."
              />
            </FadeIn>
            <FadeIn delay={0.1} className="h-full">
              <FeatureChip
                icon={Bookmark}
                title="Parameter Snapshots"
                description="Save and name a set of parameter values. Switch between configurations instantly to compare design variations."
              />
            </FadeIn>
            <FadeIn delay={0.15} className="h-full">
              <FeatureChip
                icon={SlidersHorizontal}
                title="Extended Parameter Controls"
                description="Arc dials for angles, XY and XYZ pads for spatial positioning, steppers for named sequences, and grid inputs for 2D cell layouts — all from a single comment annotation."
              />
            </FadeIn>
            <FadeIn delay={0.1} className="h-full">
              <FeatureChip
                icon={Library}
                title="Bundled Libraries"
                description="BOSL2, BOSL, MCAD, LEGO.scad, and Gridfinity are pre-installed and importable in any model — no setup required."
              />
            </FadeIn>
            <FadeIn delay={0.05} className="h-full">
              <FeatureChip
                icon={DatabaseZap}
                title="JSON & API Data"
                description="Import JSON files or fetch live external APIs to drive data-driven and generative models — parametric design powered by real-world data."
              />
            </FadeIn>
            <FadeIn delay={0.1} className="h-full">
              <FeatureChip
                icon={ImageDown}
                title="High Quality Export"
                description="Export high-resolution screenshots and renders of your model for presentations, portfolio use, or print-ready product images."
              />
            </FadeIn>
            <FadeIn delay={0.15} className="h-full">
              <FeatureChip
                icon={Palette}
                title="Texture Scripts"
                description="Generate procedural textures with JavaScript and the Canvas 2D API. Animate them, hook them up to parameters, and project them onto your geometry."
              />
            </FadeIn>
          </div>
        </section>

        <FadeIn>
          <Separator className="my-4" />
        </FadeIn>

        {/* ---------------------------------------------------------------- */}
        {/* BLOG / LEARN SECTION                                              */}
        {/* ---------------------------------------------------------------- */}
        <section className="py-16">
          <FadeIn>
            <div className="flex flex-col items-center gap-6 rounded-2xl border border-border bg-card px-8 py-14 text-center">
              <div className="rounded-lg bg-muted p-3">
                <BookOpen className="h-6 w-6 text-foreground" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Blog &amp; Tutorials
                </p>
                <h2 className="mt-3 text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                  Learn as you build
                </h2>
                <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground">
                  A growing library of tutorials, guides, and interactive
                  playgrounds — all running live OpenSCAD right in the browser.
                  From beginner name tags to generative art, multi-color
                  Gridfinity bins, and data-driven models powered by external
                  APIs.
                </p>
              </div>
              <Link to="/blog">
                <Button size="lg" className="gap-2 px-7">
                  Read the Blog
                  <span aria-hidden>→</span>
                </Button>
              </Link>
            </div>
          </FadeIn>
        </section>

        <FadeIn>
          <Separator className="my-4" />
        </FadeIn>

        {/* ---------------------------------------------------------------- */}
        {/* CTA FOOTER                                                        */}
        {/* ---------------------------------------------------------------- */}
        <section className="flex flex-col items-center py-24 text-center">
          <FadeIn>
            <div className="mb-6 flex items-center justify-center">
              <MeshpitLogo className="h-10 w-10 text-muted-foreground" />
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              Ready to build something?
            </h2>
            <p className="mt-4 text-base text-muted-foreground">
              Start with a blank file, fork a community model, or let AI design
              it for you.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link to="/new">
                <Button size="lg" className="gap-2 px-7">
                  Start Creating
                  <span aria-hidden>→</span>
                </Button>
              </Link>
              <Link to="/explore">
                <Button size="lg" variant="outline" className="gap-2 px-7">
                  Explore Models
                </Button>
              </Link>
              <Link to="/blog">
                <Button size="lg" variant="outline" className="gap-2 px-7">
                  Read the Blog
                </Button>
              </Link>
            </div>
            <p className="mt-6 text-xs text-muted-foreground/60">
              Free and open source · GPL v3 ·{' '}
              <Link
                to="/features"
                className="underline-offset-4 hover:text-muted-foreground/90 hover:underline"
              >
                Features
              </Link>
              {' · '}
              <Link
                to="/changelog"
                className="underline-offset-4 hover:text-muted-foreground/90 hover:underline"
              >
                Changelog
              </Link>
            </p>
          </FadeIn>
        </section>
      </div>
    </div>
  );
}
